import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  AcceptFamilyInviteActionProps,
  AdultInviteSuccessPayload,
  CancelAdultInviteSuccessPayload,
  FamilyState,
  FetchFamilySuccessPayload,
  UpdateChildProfilePayload,
  RequestError,
} from './types'
import type {
  AddAdultActionProps,
  CreateChildProfileActionProps,
  RecordChildConsentActionProps,
  RemoveAdultActionProps,
  UpdateChildProfileActionProps,
} from './types'

const initialState: FamilyState = {
  id: null,
  members: {},
  permissions: {},
  requestRunning: false,
  requestError: null,
  isAddingAdultMember: false,
}

const { actions, reducer } = createSlice({
  name: 'family',
  initialState,
  reducers: {
    fetchFamilySuccess: (state, action: PayloadAction<FetchFamilySuccessPayload>) => {
      const { familyId, family, permissions } = action.payload
      const familyMap = family.reduce<FamilyState['members']>((acc, member) => {
        acc[member.id] = member
        return acc
      }, {})

      state.id = familyId
      state.members = familyMap
      state.permissions = permissions
      state.requestRunning = false
      state.requestError = null
    },
    adultInviteSuccess: (state, action: PayloadAction<AdultInviteSuccessPayload>) => {
      const { member, familyId } = action.payload
      state.members[member.id] = member
      state.id = familyId
      state.requestRunning = false
      state.requestError = null
    },
    cancelAdultInviteSuccess: (state, action: PayloadAction<CancelAdultInviteSuccessPayload>) => {
      const { memberId } = action.payload
      const members = { ...state.members }

      delete members[memberId]

      state.members = members
      state.requestRunning = false
      state.requestError = null
    },
    updateChildProfileState: (state, action: PayloadAction<UpdateChildProfilePayload>) => {
      const { childProfile, familyId } = action.payload
      state.members[childProfile.id] = childProfile
      state.id = familyId
      state.requestRunning = false
      state.requestError = null
    },

    // Saga request actions
    fetchFamilyRequest: (state) => {
      state.requestRunning = true
      state.requestError = null
    },
    adultInviteRequest: (state, _action: PayloadAction<AddAdultActionProps>) => {
      state.requestRunning = true
      state.requestError = null
      state.isAddingAdultMember = true
    },
    updateChildProfileRequest: (state, _action: PayloadAction<UpdateChildProfileActionProps>) => {
      state.requestRunning = true
      state.requestError = null
    },
    createChildProfileRequest: (state, _action: PayloadAction<CreateChildProfileActionProps>) => {
      state.requestRunning = true
      state.requestError = null
    },
    cancelAdultInviteRequest: (state, _action: PayloadAction<RemoveAdultActionProps>) => {
      state.requestRunning = true
      state.requestError = null
    },

    recordChildConsentRequest: (state, _action: PayloadAction<RecordChildConsentActionProps>) => {
      state.requestRunning = true
      state.requestError = null
    },

    acceptFamilyInviteRequest: (state, _action: PayloadAction<AcceptFamilyInviteActionProps>) => {
      state.requestRunning = true
      state.requestError = null
    },
    acceptFamilyInviteSuccess: (state) => {
      state.requestRunning = false
      state.requestError = null
    },

    familyError: (state, action: PayloadAction<{ error: RequestError }>) => {
      state.requestRunning = false
      state.requestError = action.payload.error
      state.isAddingAdultMember = false
    },
    finishAdultInviteFlow: (state) => {
      state.isAddingAdultMember = false
    },
    familyClear: () =>
      ({
        id: null,
        members: {},
        permissions: {},
        requestRunning: false,
        requestError: null,
        isAddingAdultMember: false,
      } satisfies FamilyState),
  },
})

export { actions as familyActions }

export default reducer
